<template>
    <!-- A describe-then-pick choice rendered as selectable cards instead of a
         dropdown: each option shows its title + description with a radio, and the
         whole card is clickable. Picking a card IS the choice, so there is no
         separate control. Use this over a plain `select` when each option needs
         explaining (e.g. the MKT report styles). -->
    <!-- Card style from the report designs: a one-line list, the chosen row
         solid blue and the rest light gray. Each option's `cardLabel` is its
         short line; its full description is in the info tip beside it. -->
    <div    v-if="variant === 'cards'"
            class="flex flex-col gap-2"
            role="radiogroup"
            :aria-label="field.label"
    >
        <div    v-for="option in normalizedOptions"
                :key="option.value"
                class="flex items-center gap-3"
        >
            <button type="button"
                    role="radio"
                    :aria-checked="option.value === modelValue"
                    class="w-full rounded-md px-4 py-3 text-left text-base transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    :class="option.value === modelValue
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'"
                    @click="$emit('update:modelValue', option.value)"
            >{{ option.cardLabel }}</button>

            <!-- Beside the button, not in it: a focusable tip cannot sit
                 inside a button. -->
            <info-tip   v-if="option.description"
                        :text="option.description"
            ></info-tip>
        </div>

        <p  v-if="error"
            class="m-0 text-xs text-red-500">
            {{ error }}
        </p>
    </div>

    <report-field-row   v-else
                        :field-id="field.key"
                        :label="field.label"
                        :description="field.description"
                        :error="error"
    >
        <div class="flex flex-col gap-2" role="radiogroup" :aria-label="field.label">
            <button v-for="option in normalizedOptions"
                    :key="option.value"
                    type="button"
                    role="radio"
                    :aria-checked="option.value === modelValue"
                    class="flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors duration-150"
                    :class="option.value === modelValue
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'"
                    @click="$emit('update:modelValue', option.value)"
            >
                <!-- Radio indicator: a ring that fills when selected. -->
                <span   class="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border"
                        :class="option.value === modelValue ? 'border-blue-600' : 'border-gray-400'"
                >
                    <span   v-if="option.value === modelValue"
                            class="h-2 w-2 rounded-full bg-blue-600"></span>
                </span>

                <span class="min-w-0">
                    <span class="block text-sm font-medium text-gray-900">{{ option.label }}</span>
                    <span   v-if="option.description"
                            class="mt-0.5 block text-sm text-gray-500">{{ option.description }}</span>
                </span>
            </button>
        </div>
    </report-field-row>
</template>

<script>
import ReportFieldRow from "../ReportFieldRow.vue"
import InfoTip from "@/components/elements/InfoTip.vue"

export default {
    name: "ReportOptionCardsField",
    components: {ReportFieldRow, InfoTip},
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: [String, Number],
            default: null,
        },
        field: {
            type: Object,
            required: true,
        },
        // Same shape the panel resolves for `select`: keyed by option value. Each
        // value is either a plain label string, or `{ label, description }` when
        // the card should show explanatory copy.
        options: {
            type: Object,
            default: () => ({}),
        },
        error: {
            type: String,
            default: null,
        },
        // 'list' or 'cards'; see ReportSettingsPanel.
        variant: {
            type: String,
            default: 'list',
        },
    },
    computed: {
        normalizedOptions() {
            return Object.entries(this.options ?? {}).map(([value, option]) => {
                if (option !== null && typeof option === 'object') {
                    return {
                        value,
                        label: option.label,
                        description: option.description ?? null,
                        cardLabel: option.cardLabel ?? option.label,
                    }
                }
                return {value, label: option, description: null, cardLabel: option}
            })
        },
    },
}
</script>
